import { useContext, useEffect, useState } from "react";
import "./CopyModal.css";
import { Button, Checkbox, CircleX, SearchCheckList, Select } from "../../ui";
import { Context } from "../../../context/Context";
import { fetchMenuAPI } from "../../../utils/api/menu";
import { LIFECYCLE_LABELS, localized } from "../menuRole";

function ModalBlock({ title, children }) {
  return (
    <div className="modal-block">
      <h4>{title}</h4>
      <div>{children}</div>
    </div>
  );
}

// copyType:
//   "SECTION" | "SECTIONS" -> pick target menus
//   "ITEM"                 -> pick target menus + a section in each
//   "ADDITION-GROUP"       -> pick target restaurants
// `menus` are the copy destinations: every non-archived menu of the SAME
// restaurant, the current one included (the backend enforces this too).
// Copies are saved directly into the target menus.
export default function MenuSelectionModal({ menus = [], restaurants = [], copyType, onSubmit, onClose }) {
  const { lang } = useContext(Context);
  const isArabic = lang === "ar";
  const [selected, setSelected] = useState([]);
  const [sectionsByMenu, setSectionsByMenu] = useState({});
  const [targetSection, setTargetSection] = useState({});
  const pickRestaurants = copyType === "ADDITION-GROUP";

  // Sections come from each selected menu; deselecting a menu drops its section.
  useEffect(() => {
    if (copyType !== "ITEM") return;
    setTargetSection((prev) => Object.fromEntries(Object.entries(prev).filter(([menuId]) => selected.includes(menuId))));
    selected
      .filter((id) => !sectionsByMenu[id])
      .forEach(async (id) => {
        const menu = await fetchMenuAPI(id).catch(() => null);
        setSectionsByMenu((prev) => ({ ...prev, [id]: menu?.sections || [] }));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, copyType]);

  const toggleRestaurant = (id) => setSelected((prev) => (prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]));

  const canSubmit = copyType === "ITEM" ? selected.length > 0 && selected.every((id) => targetSection[id]) : selected.length > 0;

  const handleSubmit = () => {
    if (pickRestaurants) return onSubmit({ restaurantIds: selected });
    return onSubmit({ targets: selected.map((menuId) => ({ menuId, ...(copyType === "ITEM" ? { sectionId: targetSection[menuId] } : {}) })) });
  };

  const title = { SECTION: isArabic ? "نسخ القسم" : "Copy section", SECTIONS: isArabic ? "نسخ كل الأقسام" : "Copy all sections", ITEM: isArabic ? "نسخ الصنف" : "Copy item", "ADDITION-GROUP": isArabic ? "نسخ الإضافة لمطاعم أخرى" : "Copy addition to restaurants" }[copyType];
  const menuLabel = (menu) => `${menu.name} (${LIFECYCLE_LABELS[lang]?.[menu.lifecycle] || menu.lifecycle})`;

  return (
    <div className="modal-backdrop menu-copy-backdrop" onClick={onClose}>
      <div className="modal-card" role="dialog" aria-modal="true" aria-label={title} onClick={(e) => e.stopPropagation()}>
        <header className="modal-header">
          <h2>{title}</h2>
          <Button type="button" variant="warning" appearance="soft" size="sm" rounded title={isArabic ? "إغلاق" : "Close"} aria-label={isArabic ? "إغلاق" : "Close"} icon={<CircleX size={18} />} onClick={onClose} />
        </header>

        {pickRestaurants ? (
          <ModalBlock title={isArabic ? "المطاعم" : "Restaurants"}>
            {restaurants.map((restaurant) => (
              <Checkbox key={restaurant._id} checked={selected.includes(restaurant._id)} onChange={() => toggleRestaurant(restaurant._id)} label={restaurant.businessName} />
            ))}
          </ModalBlock>
        ) : (
          <ModalBlock title={isArabic ? "قوائم الطعام" : "Menus"}>
            <SearchCheckList options={menus.map((menu) => ({ value: String(menu._id), label: menuLabel(menu) }))} selected={selected} onChange={setSelected} placeholder={isArabic ? "اختر قوائم الطعام" : "Select menus"} fullWidth="full-width" />
          </ModalBlock>
        )}

        {copyType === "ITEM" &&
          selected.map((menuId) => {
            const menu = menus.find((m) => String(m._id) === menuId);
            const sections = sectionsByMenu[menuId];
            return (
              <ModalBlock key={menuId} title={`${isArabic ? "القسم في" : "Section in"} ${menu?.name || ""}`}>
                <Select
                  value={targetSection[menuId] || ""}
                  placeholder={!sections ? (isArabic ? "جاري التحميل..." : "Loading...") : isArabic ? "اختر القسم" : "Select section"}
                  disabled={!sections?.length}
                  options={(sections || []).map((section, index) => ({ value: String(section._id), label: `${index + 1}. ${localized(section.name, lang)}` }))}
                  onChange={(e) => setTargetSection((prev) => ({ ...prev, [menuId]: e.target.value }))}
                />
              </ModalBlock>
            );
          })}

        <footer className="modal-footer">
          <Button variant="primary" appearance="filled" className="modal-primary" disabled={!canSubmit} onClick={handleSubmit}>
            {isArabic ? "نسخ" : "Copy"}
          </Button>
        </footer>
      </div>
    </div>
  );
}
